import OrdersDuplicationView from '@/views/OrdersDuplicationView.vue';
import ExceptionReports from '@/views/admin/ExceptionReports.vue';
import SelfSellInView from '@/views/self-sell-in/SelfSellInView.vue';
import { CaseType } from '@onrunning/backstage-models';
import { createRouter, createWebHistory, type NavigationGuardNext, type RouteLocationNormalized } from 'vue-router';
import { useRouterHelpers } from '../composables/router-helpers';
import AdminLayout from '../layouts/AdminLayout.vue';
import MainLayout from '../layouts/MainLayout.vue';
import { AddEditCaseBreadcrumbSteps } from '../models/add-edit-case-breadcrumb-steps.enum';
import { AddEditOrderBreadcrumbSteps } from '../models/add-edit-order-breadcrumb-steps.enum';
import { FeaturesService } from '../services/features.service';
import { useAddEditOrderStore } from '../stores/add-edit-order';
import { useUserStore } from '../stores/user';
import AccountingView from '../views/AccountingView.vue';
import AddEditCaseView from '../views/AddEditCaseView.vue';
import AddEditCaseViewV3 from '../views/AddEditCaseViewV3.vue';
import AddEditOrderView from '../views/AddEditOrderView.vue';
import AddEditOrderViewV3 from '../views/AddEditOrderViewV3.vue';
import CasesView from '../views/CasesView.vue';
import ContactUsView from '../views/ContactUsView.vue';
import CreatePasswordView from '../views/CreatePasswordView.vue';
import DocumentsCheckoutView from '../views/DocumentsCheckoutView.vue';
import FaqsView from '../views/FaqsView.vue';
import HomeView from '../views/HomeView.vue';
import LaunchCalendarView from '../views/LaunchCalendarView.vue';
import LoginView from '../views/LoginView.vue';
import MarketingView from '../views/MarketingView.vue';
import OrderView from '../views/OrderView.vue';
import PaymentEmailUnsubscriptionView from '../views/PaymentEmailUnsubscriptionView.vue';
import ProductsAvailabilityView from '../views/ProductsAvailabilityView.vue';
import ProductsAvailabilityViewV2 from '../views/ProductsAvailabilityViewV2.vue';
import ProductsView from '../views/ProductsView.vue';
import ResetPasswordView from '../views/ResetPasswordView.vue';
import SalesforceLoginCallbackView from '../views/SalesforceLoginCallbackView.vue';
import ShipmentEmailUnSubscriptionView from '../views/ShipmentEmailUnSubscriptionView.vue';
import SuccessOrderedView from '../views/SuccessOrderedView.vue';
import SummaryView from '../views/SummaryView.vue';
import HealthStatusView from '../views/admin/HealthStatusView.vue';
import OrdersDeleteView from '../views/admin/OrdersDeleteView.vue';
import OrdersRetryView from '../views/admin/OrdersRetryView.vue';
import ProductStyleExceptionsView from '../views/admin/ProductStyleExceptionsView.vue';

export enum RouteNames {
  Login = 'login',
  SalesforceLoginCallback = 'login-sf-callback',
  OutdatedSalesforceLoginCallback = 'login-outdated-sf-oauth-callback',
  ResetPassword = 'reset-password',
  CreatePassword = 'create-password',
  Home = 'home',
  Faqs = 'view-faqs',
  FaqsSection = 'view-faqs-section',
  ViewOrder = 'view-order',
  ViewDraft = 'view-draft',
  EditOrder = 'edit-order',
  CreateOrderForSalesRep = 'create-order-for-sales-rep',
  CreateOrderForDealerDefault = 'create-order-for-dealer-default-step',
  CreateOrderForDealer = 'create-order-for-dealer',
  Marketing = 'marketing',
  Accounting = 'accounting',
  DocumentsCheckout = 'documents-checkout',
  CaseView = 'view-case',
  CreateWarrantyCase = 'create-warranty-case',
  CreateReturnCase = 'create-return-case',
  ViewReturnCases = 'view-return-cases',
  ViewReturnCase = 'view-return-case',
  ViewWarrantyCases = 'view-warranty-cases',
  ViewWarrantyCase = 'view-warranty-case',
  ContactUs = 'contact-us',
  Summary = 'summary',
  ProductsAvailabilityDefault = 'products-availability-default-step',
  ProductsAvailability = 'products-availability',
  ViewProducts = 'view-products',
  SuccessOrdered = 'success-ordered',
  ViewOrders = 'view-orders',
  CreateWarrantyCaseDefault = 'warranty-cases-create-default-step',
  CreateWarrantyCaseProductsSelection = 'warranty-cases-create-products-selection',
  CreateWarrantyCaseQuantitiesSelection = 'warranty-cases-create-quantities-selection',
  CreateReturnCaseDefault = 'return-cases-create-default-step',
  CreateReturnCaseProductsSelection = 'return-cases-create-products-selection',
  CreateReturnCaseQuantitiesSelection = 'return-cases-create-quantities-selection',
  ViewLaunchCalendar = 'view-launch-calendar',
  OrdersDuplicationView = 'view-orders-duplication',
  UnsubscribePaymentsEmailView = 'unsubscribe-payments-emails',
  UnsubscribeShipmentEmailView = 'unsubscribe-shipment-emails',
  SelfSellInView = 'self-sell-in',
}

const { getStartUrl } = useRouterHelpers();
const isMatrixRefactorEnabled = import.meta.env.VITE_APP_enableMatrixRefactor === 'true';

// ToDo: By default now history mode would be used, so no more hash in url
// this means we will need to change SF login callback url, SEO tools and url on emails notifications.
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    if (to.hash) {
      // The 300ms delay gives the page time to finish rendering before Vue Router attempts to find and scroll to the element.
      return new Promise((resolve) => {
        setTimeout(() => resolve({ el: to.hash, behavior: 'smooth' }), 300);
      });
    }

    return { top: 0 };
  },
  routes: [
    // main layout
    {
      path: '/',
      component: MainLayout,
      children: [
        {
          path: '',
          name: RouteNames.Home,
          component: HomeView,
        },
        ...(FeaturesService.enableSelfSellIn
          ? [
              {
                path: 'self-sell-in',
                name: RouteNames.SelfSellInView,
                component: SelfSellInView,
                meta: { usesExitNavBar: true },
              },
            ]
          : []),
        {
          path: 'orders',
          name: RouteNames.ViewOrders,
          component: HomeView,
          props: true,
        },
        {
          path: '/orders/:id',
          name: RouteNames.ViewOrder,
          props: true,
          component: OrderView,
        },
        {
          path: '/drafts/:id',
          name: RouteNames.ViewDraft,
          props: true,
          component: OrderView,
        },
        {
          path: 'faqs',
          name: RouteNames.Faqs,
          component: FaqsView,
        },
        {
          path: '/faqs/:id',
          name: RouteNames.FaqsSection,
          component: FaqsView,
          props: true,
        },
        {
          path: 'marketing',
          name: RouteNames.Marketing,
          component: MarketingView,
        },
        {
          path: 'warranty-cases',
          name: RouteNames.ViewWarrantyCases,
          component: CasesView,
          props: () => ({ caseType: CaseType.Warranty }),
        },
        {
          path: 'return-cases',
          name: RouteNames.ViewReturnCases,
          component: CasesView,
          props: () => ({ caseType: CaseType.Return }),
        },
        {
          path: '/launch-calendar',
          name: RouteNames.ViewLaunchCalendar,
          component: LaunchCalendarView,
        },
        {
          path: '/orders-duplication',
          name: RouteNames.OrdersDuplicationView,
          component: OrdersDuplicationView,
          props: true,
        },
        {
          path: 'contact-us',
          name: RouteNames.ContactUs,
          component: ContactUsView,
        },
        {
          path: 'accounting',
          name: RouteNames.Accounting,
          component: AccountingView,
          props: (route) => ({
            orderRef: route.query.orderRef || route.query.paymentId,
            redirectResult: route.query.redirectResult,
          }),
        },
        {
          path: 'accounting/checkout/:documentReferenceIds',
          name: RouteNames.DocumentsCheckout,
          component: DocumentsCheckoutView,
          props: true,
        },
        {
          path: '/summary',
          name: RouteNames.Summary,
          component: SummaryView,
        },
        {
          path: '/products-availability/',
          name: RouteNames.ProductsAvailabilityDefault,
          component: isMatrixRefactorEnabled ? ProductsAvailabilityViewV2 : ProductsAvailabilityView,
          redirect: `/products-availability/${AddEditCaseBreadcrumbSteps.productsModelsSelection}`,
          children: [
            {
              name: RouteNames.ProductsAvailability,
              path: '/products-availability/:step',
              component: isMatrixRefactorEnabled ? ProductsAvailabilityViewV2 : ProductsAvailabilityView,
            },
          ],
        },
        {
          path: '/products',
          name: RouteNames.ViewProducts,
          component: ProductsView,
        },
        {
          path: '/success-ordered/:orderType/:orderNumber',
          name: RouteNames.SuccessOrdered,
          component: SuccessOrderedView,
          props: true,
        },
        // admin layout
        {
          path: '/admin',
          component: AdminLayout,
          name: 'admin',
          redirect: 'admin/retry-orders',
          children: [
            // ToDo: combine the OrderRetry and OrderDelete components in a single reusable one
            {
              name: 'retry-orders',
              path: 'retry-orders',
              component: OrdersRetryView,
            },
            {
              name: 'delete-orders',
              path: 'delete-orders',
              component: OrdersDeleteView,
            },
            {
              name: 'health-status',
              path: 'health',
              component: HealthStatusView,
            },
            {
              name: 'product-style-exceptions',
              path: 'product-style-exceptions',
              component: ProductStyleExceptionsView,
            },
            {
              name: 'exception-reports',
              path: 'exception-reports',
              component: ExceptionReports,
            },
          ],
        },
        {
          path: '/unsubscribe-payment-email/:userId/:token',
          name: RouteNames.UnsubscribePaymentsEmailView,
          component: PaymentEmailUnsubscriptionView,
          props: true,
        },
        {
          path: '/unsubscribe-shipment-email/:userId/:token',
          name: RouteNames.UnsubscribeShipmentEmailView,
          component: ShipmentEmailUnSubscriptionView,
          props: true,
        },
      ],
    },
    // exit navbar layout
    {
      path: '/',
      component: MainLayout,
      children: [
        {
          path: '/orders/:id/edit-products/:editType(Order|Draft)/:step',
          name: RouteNames.EditOrder,
          component: isMatrixRefactorEnabled ? AddEditOrderViewV3 : AddEditOrderView,
          props: true,
        },
        {
          path: '/orders/create/:orderType/:startDate/:contactId/:step',
          name: RouteNames.CreateOrderForSalesRep,
          component: isMatrixRefactorEnabled ? AddEditOrderViewV3 : AddEditOrderView,
          props: true,
        },
        {
          path: '/orders/create/:orderType/:startDate/',
          name: RouteNames.CreateOrderForDealerDefault,
          component: isMatrixRefactorEnabled ? AddEditOrderViewV3 : AddEditOrderView,
          props: true,
          redirect: `/orders/create/:orderType/:startDate/${AddEditOrderBreadcrumbSteps.productsModelsSelection}`,
          children: [
            {
              path: '/orders/create/:orderType/:startDate/:step',
              name: RouteNames.CreateOrderForDealer,
              component: AddEditOrderView,
              props: true,
            },
          ],
        },
        {
          path: `/warranty-cases/create/`,
          name: RouteNames.CreateWarrantyCaseDefault,
          component: isMatrixRefactorEnabled ? AddEditCaseViewV3 : AddEditCaseView,
          props: () => ({
            caseType: CaseType.Warranty,
          }),
          redirect: {
            name: RouteNames.CreateWarrantyCase,
            params: { step: AddEditCaseBreadcrumbSteps.productsModelsSelection },
          },
          children: [
            {
              path: `/warranty-cases/create/:step`,
              name: RouteNames.CreateWarrantyCase,
              component: isMatrixRefactorEnabled ? AddEditCaseViewV3 : AddEditCaseView,
              props: (route) => ({
                caseType: CaseType.Warranty,
                step: route.params.step,
              }),
            },
          ],
        },
        {
          path: 'warranty-cases/:id/:step',
          name: RouteNames.ViewWarrantyCase,
          component: isMatrixRefactorEnabled ? AddEditCaseViewV3 : AddEditCaseView,
          props: (route) => ({
            caseType: CaseType.Warranty,
            id: route.params.id,
            step: route.params.step,
          }),
        },
        {
          path: `/return-cases/create/`,
          name: RouteNames.CreateReturnCaseDefault,
          component: isMatrixRefactorEnabled ? AddEditCaseViewV3 : AddEditCaseView,
          props: () => ({
            caseType: CaseType.Return,
          }),
          redirect: {
            name: RouteNames.CreateReturnCase,
            params: { step: AddEditCaseBreadcrumbSteps.productsModelsSelection },
          },
          children: [
            {
              path: `/return-cases/create/:step`,
              name: RouteNames.CreateReturnCase,
              component: isMatrixRefactorEnabled ? AddEditCaseViewV3 : AddEditCaseView,
              props: (route) => ({
                caseType: CaseType.Return,
                step: route.params.step,
              }),
            },
          ],
        },
        {
          path: 'return-cases/:id/:step',
          name: RouteNames.ViewReturnCase,
          component: isMatrixRefactorEnabled ? AddEditCaseViewV3 : AddEditCaseView,
          props: (route) => ({
            caseType: CaseType.Return,
            id: route.params.id,
            step: route.params.step,
          }),
        },
      ],
      // ToDO: switch to a new layout, ie ExitNavBarLayout
      meta: { usesExitNavBar: true },
    },
    // logged out layout
    {
      path: '/',
      name: 'logged-out',
      children: [
        {
          path: 'login',
          name: 'login-main',
          children: [
            {
              path: '',
              name: RouteNames.Login,
              component: LoginView,
            },
            {
              // we need to change the callback url on backstage-api and sf for this to work out of the box
              path: 'oauth/callback',
              name: RouteNames.SalesforceLoginCallback,
              // component is lazy-loaded only when the route is visited.
              component: SalesforceLoginCallbackView,
            },
          ],
        },
        {
          path: 'SiteLogin',
          name: 'login-outdated-sf-oauth-callback',
          component: SalesforceLoginCallbackView,
        },
        {
          path: 'reset-password',
          name: RouteNames.ResetPassword,
          component: ResetPasswordView,
        },
        {
          path: '/create-password/:token',
          name: RouteNames.CreatePassword,
          component: CreatePasswordView,
          props: true,
        },
        {
          path: '/unsubscribe-payment-email/:userId/:token',
          name: RouteNames.UnsubscribePaymentsEmailView,
          component: PaymentEmailUnsubscriptionView,
          props: true,
        },
        {
          path: '/unsubscribe-shipment-email/:userId/:token',
          name: RouteNames.UnsubscribeShipmentEmailView,
          component: ShipmentEmailUnSubscriptionView,
          props: true,
        },
      ],
    },
  ],
});

router.beforeEach((to, from, next) => {
  // todo: get rid of this code after we update the api not to generate a hashed url
  if (to.fullPath.includes('#/create-password/')) {
    const url = to.fullPath;
    const username = decodeURIComponent(url.split('username=')[1]);
    const token = url.split('#/create-password/')[1]?.split('?')[0];

    if (!token || !username) {
      return next({ name: RouteNames.Home });
    } else {
      return next({
        name: RouteNames.CreatePassword,
        params: { token },
        query: { username },
        replace: true,
      });
    }
  }

  if (isUnauthenticatedEmailUnsubscribeRoute(to)) {
    return next();
  }

  // this condition needs to be updated in case we add more pages where authentication is not required
  const pageRequiresAuthentication =
    to.name &&
    ![
      RouteNames.Login.toLowerCase(),
      RouteNames.SalesforceLoginCallback.toLowerCase(),
      RouteNames.ResetPassword.toLowerCase(),
      RouteNames.CreatePassword.toLowerCase(),
      RouteNames.OutdatedSalesforceLoginCallback.toLowerCase(),
    ].includes(to.name?.toString().toLowerCase());

  if (pageRequiresAuthentication && !useUserStore().isAuthenticated) {
    const startUrl = getStartUrl();

    return next({
      name: RouteNames.Login,
      ...(startUrl && { query: { startURL: getStartUrl() } }),
    });
  } else if (!pageRequiresAuthentication && useUserStore().isAuthenticated) {
    return next({ name: RouteNames.Home });
  }

  if (checkIfRedirectPayment(to, next)) {
    return;
  }

  const { $reset: resetAddEditOrderStore } = useAddEditOrderStore();

  if (!to.path.startsWith('/orders/create') && !to.path.includes('/edit-products')) {
    resetAddEditOrderStore();
  }

  next();
});

function checkIfRedirectPayment(to: RouteLocationNormalized, next: NavigationGuardNext) {
  const has3DSResult = !!(to.query.redirectResult || to.fullPath.includes('redirectResult'));

  if (!has3DSResult) return false;

  if (to.fullPath.includes('#/accounting') || to.fullPath.includes('/accounting')) {
    next(to.fullPath.replace('/#/', '/'));

    return true;
  }

  return false;
}

function isUnauthenticatedEmailUnsubscribeRoute(to: RouteLocationNormalized) {
  const unsubscribeRoutes = [RouteNames.UnsubscribePaymentsEmailView, RouteNames.UnsubscribeShipmentEmailView];

  return (
    unsubscribeRoutes.some((route) => route.toLowerCase() === to.name?.toString().toLowerCase()) &&
    !useUserStore().isAuthenticated
  );
}

export default router;
